<template>
  <div id="buscador-acuerdos">

    <div class="row-content d-flex mt-2">
      <filtros-primer-nivel-buscador
        ref="filtros_primer_nivel_buscador"
        @filtros:actualizar="filtrosAcuerdos($event)"
      ></filtros-primer-nivel-buscador>
    </div>

    <ca-empty-state
      :show="!busqueda_realizada"
      icon="search"
      texto_principal="Buscá acuerdos comerciales"
      class="mt-10">
      <div slot="texto-secundario">
        <p class="texto-secundario">Acuerdos por agente de ventas, tipo y período</p>
      </div>
    </ca-empty-state>

    <div v-if="busqueda_realizada" class="tabla-container">

      <!-- Tabla de acuerdos comerciales -->
      <tabla-acuerdos-comerciales
        ref="tabla_acuerdos_comerciales"
        :acuerdos_comerciales="acuerdos_comerciales"
        :tipo_acuerdo_filtros_primer_nivel="filtros_primer_nivel.tipo_acuerdo"
      >
      </tabla-acuerdos-comerciales>

    </div>

  </div>
</template>

<script>
import { EventBus } from '@/main';
import { LAYOUT_MUTATION_MODAL_ERROR, LAYOUT_MUTATION_MOSTRAR_LOADING, LAYOUT_MUTATION_OCULTAR_LOADING } from '@/store/layout/layout.types';
import FiltrosPrimerNivelBuscador from './FiltrosPrimerNivelBuscador.vue';
import { APIAcuerdoComercial } from '../api/acuerdo-comercial.api';
import { AccionesSobreAcuerdosComerciales, getTituloTipoConsorcio } from '../acuerdo-comercial.consts';
import { trimUsuarioSales } from '../acuerdo-comercial.helpers';
import { formatFecha } from '@/components/vue_filters';
import TablaAcuerdosComerciales from './TablaAcuerdosComerciales.vue';
import { ENDPOINT_GETTER } from '@/store/endpoints/endpoints.types';

export default {
  name: 'BuscadorAcuerdos',
  components: {
    TablaAcuerdosComerciales,
    FiltrosPrimerNivelBuscador
  },
  data: () => ({
    acuerdos_comerciales: [],
    filtros_primer_nivel: {},
    busqueda_realizada: false,
  }),
  created() {

    /**
     * Hook para reaccionar ante el evento de eliminar un acuerdo comercial
     */
    EventBus.$on('acuerdo-comercial:eliminar', (acuerdo_comercial_eliminado) => {
      this.actualizarAcuerdoEliminado(acuerdo_comercial_eliminado);
    });
    /**
     * Hook para reaccionar ante el evento de extender el tiempo de trial de un acuerdo comercial
     * Actualiza el acuerdo comercial en el arreglo de acuerdos comerciales en curso y de esta manera la tabla a través del watch detectará el cambio y se actualizará
     */
    EventBus.$on('acuerdo-comercial:extender-trial', (acuerdo_comercial_extender_trial) => {
      this.actualizarAcuerdoTrialExtendido(acuerdo_comercial_extender_trial);
    });

    /**
     * Hook para reaccionar ante el evento de finalizar el periodo de aceptación de un acuerdo comercial
     */
    EventBus.$on('acuerdo-comercial:finalizar-trial', (acuerdo_comercial_finalizar_trial) => {
      this.actualizarAcuerdoFinalizarTrial(acuerdo_comercial_finalizar_trial);
    });

    /**
     * Hook para reaccionar ante el evento de editar un acuerdo comercial
     */
    EventBus.$on('acuerdo-comercial:editado', (acuerdo_comercial_editado) => {
      this.actualizarAcuerdoEditado(acuerdo_comercial_editado);
    });

    /**
     * Hook para reaccionar ante el evento de creación de acuerdos comerciales
     */
    EventBus.$on('acuerdo-comercial:crear', (acuerdos_comerciales_creados) => {
      this.actualizarAcuerdosCreados(acuerdos_comerciales_creados);
    });
  },
  /**
   *  Elimino los listeners para evitar comportamientos inesperados
   */
  beforeDestroy() {
    EventBus.$off('acuerdo-comercial:eliminar');
    EventBus.$off('acuerdo-comercial:editado');
    EventBus.$off('acuerdo-comercial:crear');
    EventBus.$off('acuerdo-comercial:extender-trial');
    EventBus.$off('acuerdo-comercial:finalizar-trial');
  },
  methods: {
    getTituloTipoConsorcio, // Necesario para poder acceder desde template
    trimUsuarioSales,
    formatFecha,

    /**
     * Metodo hook para capturar los filtros, y ejecutar obtener los acuerdos
     */
    async filtrosAcuerdos(filtros){
      this.filtros_primer_nivel = filtros; // Actualizó los filtros de primer nivel

      await this.getAcuerdosComerciales(filtros);

      // Como la referencia esta dentro de un bloque condicionado es necesario validar que esa condición sea verdadera para poder usar el ref
      if(this.busqueda_realizada) {
        this.$refs.tabla_acuerdos_comerciales.reiniciarFiltros(); // Reinicio los filtros de la tabla
      }

      this.busqueda_realizada = true;
    },

    /**
     * Metodo para recuperar todos los AC con el filtro aplicado
     */
    async getAcuerdosComerciales(params = {}){

      const url = this.$store.getters[ENDPOINT_GETTER]('propuestas');
      if(!url) {
        return;
      }

      this.$store.commit(LAYOUT_MUTATION_MOSTRAR_LOADING);
      await APIAcuerdoComercial.getAcuerdosComerciales(url, params)
        .then((resp) => {
          this.acuerdos_comerciales = resp;
          this.busqueda_realizada = true;
        })
        .catch((err) => {
          console.log(err);
          this.$store.commit(LAYOUT_MUTATION_MODAL_ERROR, {
            titulo: err.title,
            texto: err.detail
          });
        })
        .finally(() => {
          this.$store.commit(LAYOUT_MUTATION_OCULTAR_LOADING);
        });
    },

    /**
     * Metodo para eliminar un acuerdo de la lista, para evitar hacer un nuevo GET de la lista de este componente
     */
    actualizarAcuerdoEliminado(acuerdo_eliminado){

      // Actualizo la lista de acuerdos
      let index = this.acuerdos_comerciales.findIndex((ac) => ac.id === acuerdo_eliminado.id);
      index !== -1 ? this.acuerdos_comerciales.splice(index, 1) : null;

      // Calculamos headers nuevamente porque puede que el AC eliminado sea el que agregó las columnas opcionales
      this.actualizarHeadersTabla();
    },

    /**
     * Metodo para actualizar los acuerdos filtradas con el acuerdo comercial editado
     */
    actualizarAcuerdoEditado(acuerdo_editado){
      let index = this.acuerdos_comerciales.findIndex((acuerdo_comercial) => acuerdo_comercial.id === acuerdo_editado.id);
      index !== -1 ? this.acuerdos_comerciales.splice(index, 1, acuerdo_editado) : null;
    },

    /**
     * Metodo para actualizar la lista de acuerdos con los acuerdos comerciales creados. Se agregan al final de la lista de acuerdos porque al ser acuerdos nuevos son los que tienen el estado con menos prioridad
     * 
     * Al agregar estos nuevos acuerdos, reiniciamos este tab completo (lo dejamos en empty state)
     */
    actualizarAcuerdosCreados(acuerdos_comerciales_creados) {

      // Agrego los nuevos acuerdos al final de la lista (puede ser uno o varios)
      this.acuerdos_comerciales.push(...acuerdos_comerciales_creados);

      // Reinicio los filtros del buscador
      if(this.$refs.filtros_primer_nivel_buscador) {
        this.$refs.filtros_primer_nivel_buscador.reiniciarFiltros();
      }

      // Ponemos este tab en empty state
      this.busqueda_realizada = false;
    },

    /**
     * Metodo para actualizar todos los atributos referidos al estado del trial
     * Con esto evitamos hacer un nuevo GET de la lista de este componente
     */
    actualizarAcuerdoTrialExtendido(acuerdo_trial_extendido) {
      const indice = this.acuerdos_comerciales.findIndex(acuerdo_comercial => acuerdo_comercial.id === acuerdo_trial_extendido.id);
      if (indice !== -1){
        this.acuerdos_comerciales[indice].propuesta_consorcio.dias_restantes = acuerdo_trial_extendido.propuesta_consorcio.dias_restantes;
        this.acuerdos_comerciales[indice].propuesta_consorcio.fecha_fin_trial = acuerdo_trial_extendido.propuesta_consorcio.fecha_fin_trial;
        this.acuerdos_comerciales[indice].fecha_cambio_estado_actual = acuerdo_trial_extendido.fecha_cambio_estado_actual;

        if(this.acuerdos_comerciales[indice].expirada) {
          this.acuerdos_comerciales[indice].expirada = false;
        }

        // Si el acuerdo comercial no tiene la acción de finalizar período de aceptación, se la agregamos
        if (!this.acuerdos_comerciales[indice].acciones_posibles.includes(AccionesSobreAcuerdosComerciales.FINALIZAR_PERIODO_ACEPTACION)) {
          this.acuerdos_comerciales[indice].acciones_posibles.push(AccionesSobreAcuerdosComerciales.FINALIZAR_PERIODO_ACEPTACION);
        }

        this.actualizarHeadersTabla();
      }
    },

    /**
     * Metodo que actualiza todos los atributos referidos al estado del trial, ahora el estado del acuerdo es expirado
     * Con esto evitamos hacer un nuevo GET de la lista de este componente
     */
    actualizarAcuerdoFinalizarTrial(acuerdo_trial_finalizado) {
      const indice = this.acuerdos_comerciales.findIndex(acuerdo_comercial => acuerdo_comercial.id === acuerdo_trial_finalizado.id);

      if(indice !== -1) {
        this.acuerdos_comerciales[indice].propuesta_consorcio.dias_restantes = null;
        this.acuerdos_comerciales[indice].propuesta_consorcio.fecha_fin_trial = acuerdo_trial_finalizado.propuesta_consorcio.fecha_fin_trial;
        this.acuerdos_comerciales[indice].fecha_cambio_estado_actual = acuerdo_trial_finalizado.fecha_cambio_estado_actual;
        this.acuerdos_comerciales[indice].expirada = true;
        this.acuerdos_comerciales[indice].acciones_posibles = this.acuerdos_comerciales[indice].acciones_posibles.filter(accion => accion !== AccionesSobreAcuerdosComerciales.FINALIZAR_PERIODO_ACEPTACION); // Una vez finalizado el período de aceptación, ya no se puede volver a finalizarlo
      }

      this.actualizarHeadersTabla();
    },

    /**
     * Metodo para actualizar los headers de la tabla
     * Debido a que, con los cambios nuevos, se puede habilitar columnas opcionales que antes no se estaban mostrando o viceversa
     */
    actualizarHeadersTabla() {
      if(this.$refs.tabla_acuerdos_comerciales) {
        this.$refs.tabla_acuerdos_comerciales.actualizarMostrarColumnasOpcionales();
      }
    }
  },
};
</script>

<style lang="scss">
#buscador-acuerdos{
  .tabla-container{
    padding: 10px 0;

    .row-content {
      margin: 0px;
      padding: 4px 12px !important;
    }

    .pre-tabla {
      display:flex;
      align-items: center;
      margin-bottom: 10px;
    }
  }
}
</style>